import { NormalizedDropdownItemType } from '@entur/dropdown';
import { ChartOfAccountsDtoV2, ChartOfAccountsRptV2, DropdownRptV2, PartnerRptV2 } from 'types';
import {
  createEmptyDatasetSubscriptionRow,
  formatDatasetSubscriptionsForBackend,
  formatDatasetSubscriptionsForFrontend,
} from 'utils';
import { ChartOfAccountsInputType } from './types';

interface ChartOfAccountsValueMap {
  id: number;
  code: string;
}

export function getChartOfAccountsLabel(chartOfAccounts?: ChartOfAccountsDtoV2 | ChartOfAccountsRptV2 | null): string {
  const name = chartOfAccounts?.name ?? 'Ukjent';
  const code = chartOfAccounts?.code ?? 'Ukjent';
  return `${name} (${code})`;
}

export function getChartOfAccountsDropdownItems<T extends keyof ChartOfAccountsValueMap = 'id'>(
  chartsOfAccounts: Array<ChartOfAccountsDtoV2 | ChartOfAccountsRptV2> | null | undefined,
  valueField?: T
): NormalizedDropdownItemType<ChartOfAccountsValueMap[T]>[] {
  const field = valueField ?? 'id';
  if (!chartsOfAccounts) return [];

  return chartsOfAccounts
    .filter((chart) => chart.name != null && chart[field] != null)
    .map((chart) => ({
      label: getChartOfAccountsLabel(chart),
      value: chart[field]! as ChartOfAccountsValueMap[T],
    }));
}

export function formatForFrontend(
  chartOfAccounts: ChartOfAccountsDtoV2,
  reportTemplates: DropdownRptV2[],
  partners: PartnerRptV2[]
): ChartOfAccountsInputType {
  return {
    ...chartOfAccounts,
    // This API returns null for fields the generated types declare non-nullable (see
    // agmtAdjustmentId/acctRuleAdjustmentId in the recorded payloads), and the form now
    // forwards this value to the PUT instead of overwriting it.
    isAccountingMappingEnabled: chartOfAccounts.isAccountingMappingEnabled ?? false,
    datasetSubscriptions: formatDatasetSubscriptionsForFrontend(
      chartOfAccounts.datasetSubscriptions,
      reportTemplates,
      partners
    ),
  };
}

// The create form's initial state. Shared with ChartsOfAccountsCreate rather than inlined there so
// that utils.test.ts pins the *real* serialised POST payload: routeFromHAR compares POST bodies
// byte-for-byte, so the key order below is load-bearing for the create e2e spec. Reordering these
// keys, or dropping one, must fail in vitest rather than as an opaque missing-toast e2e error.
// `ref` is excluded because formatForBackend derives it from the code.
export const createChartOfAccountsDefaults = (): Omit<ChartOfAccountsInputType, 'ref'> => ({
  name: '',
  code: '',
  status: 1,
  isAccountingMappingEnabled: false,
  agmtAdjustmentId: undefined,
  acctRuleAdjustmentId: undefined,
  datasetSubscriptions: [createEmptyDatasetSubscriptionRow()],
});

export const formatForBackend = (chartOfAccounts: ChartOfAccountsInputType): ChartOfAccountsDtoV2 => {
  return {
    ...chartOfAccounts,
    ref: `EOS:ChartOfAccounts:${chartOfAccounts.code}`,
    datasetSubscriptions: formatDatasetSubscriptionsForBackend(chartOfAccounts.datasetSubscriptions),
  };
};
